import React, { useMemo } from 'react';
import { Card, Alert, Button } from 'react-bootstrap';
import { Form, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { filter, map } from 'lodash';
import { TrixEditor } from 'react-trix';
import { useUpdateEffect } from 'usehooks-ts';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FormGroupControl, FormGroupSelect } from '@components/Base';
import { useInsurersFetch } from '@components/Feature/Insurer';
import { useBenefitTemplatesFetch } from '@components/Feature/BenefitTemplate';
import { ActivationDateControl } from '@components/Feature/Quote';
import { InsuranceTypesEnum, tInsuranceTypeEnum } from '@models/InsuranceType';
import { PolicyTypes, tPolicyType } from '@models/policyType';
import { noob } from '@utils/typescript';
import { ActivationPeriods } from '@models/InsurancePolicy';
import { useCompaniesFetch } from '@components/Feature/Company';

import { FormDisabled, FormRequired, FormValues } from './type';

const GROUP_PROPS = { className: 'mb-3' };

type Props = {
  disabled?: FormDisabled;
  required: FormRequired;
};

const InsurancePolicyBuyForm = ({ disabled, required }: Props) => {
  const { t } = useTranslation();
  const insurers = useInsurersFetch();
  const benefitTemplates = useBenefitTemplatesFetch();
  const companies = useCompaniesFetch();
  const { values, isSubmitting, setFieldValue } = useFormikContext<FormValues>();
  const { insuranceType, policyType, insurerId } = values;
  const filteredBenefitTemplates = useMemo(() => {
    return filter(benefitTemplates, (x) => x.insurerId == insurerId && x.insuranceType == insuranceType && x.policyType == policyType)
  }, [benefitTemplates, insurerId, policyType, insuranceType]);

  useUpdateEffect(() => {
    if (policyType === PolicyTypes.Commercial) return;
    setFieldValue('companyId', '');
  }, [policyType])

  return (
    <Form>
      <Alert variant="warning">{t('personalInfo.changeabilityWarningMessage')}</Alert>

      <Card>
        <Card.Body>
          <Card.Title>{t('common:fillPolicyDetails')}</Card.Title>

          <FormGroupSelect
            name="insurerId"
            label={t('common:insurer')}
            required={required.insurerId}
            disabled={disabled?.insurerId}
            groupProps={GROUP_PROPS}
          >
            <option></option>
            {map(insurers, ({ id, name }) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </FormGroupSelect>

          <FormGroupSelect
            name="insuranceType"
            label={t('common:insuranceType')}
            required={required.insuranceType}
            disabled={disabled?.insuranceType}
            groupProps={GROUP_PROPS}
          >
            <option></option>
            {map(InsuranceTypesEnum, (type) => (
              <option key={type} value={type}>
                {tInsuranceTypeEnum(type)}
              </option>
            ))}
          </FormGroupSelect>

          <FormGroupSelect
            name="policyType"
            label={t('common:policyType')}
            required={required.policyType}
            disabled={disabled?.policyType}
            groupProps={GROUP_PROPS}
          >
            <option></option>
            {map(PolicyTypes, (type) => (
              <option key={type} value={type}>
                {tPolicyType(type)}
              </option>
            ))}
          </FormGroupSelect>

          <FormGroupSelect
            name="companyId"
            label={t('common:company')}
            disabled={policyType !== PolicyTypes.Commercial}
            required={policyType === PolicyTypes.Commercial}
            groupProps={GROUP_PROPS}
          >
            <option></option>
            {map(companies, ({ id, name }) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </FormGroupSelect>

          <FormGroupSelect
            multiple
            name="benefitTemplateIds"
            label={t('common:benefitTemplate')}
            required={required.benefitTemplateIds}
            disabled={disabled?.benefitTemplateIds}
            groupProps={GROUP_PROPS}
          >
            <option></option>
            {map(filteredBenefitTemplates, ({ id, name }) => (
              <option key={name} value={id}>
                {name}
              </option>
            ))}
          </FormGroupSelect>

          <ActivationDateControl
            noMin
            noMax
            required={required.activationDate}
            disabled={disabled?.activationDate}
          />

          <FormGroupSelect
            name="activationPeriod"
            label={t('common:activationPeriod')}
            required={required.activationPeriod}
            disabled={disabled?.activationPeriod}
            groupProps={GROUP_PROPS}
          >
            <option></option>
            {map(ActivationPeriods, (period) => (
              <option key={period} value={period}>
                {period}
              </option>
            ))}
          </FormGroupSelect>

          <FormGroupControl
            name="adminFeeAmount"
            label="Admin Fee"
            required={required.adminFeeAmount}
            disabled={disabled?.adminFeeAmount}
            groupProps={GROUP_PROPS}
            type="number"
          />

          <FormGroupControl
            name="proof"
            type="file"
            label={t('proof')}
            required={required.proof}
            disabled={disabled?.proof}
            groupProps={GROUP_PROPS}
          />

          <FormGroupControl
            name="note"
            as="textarea"
            rows={4}
            label="Note"
            required={required.note}
            disabled={disabled?.note}
            groupProps={GROUP_PROPS}
          />
        </Card.Body>

        <Card.Body>
          <Card.Title className="mb-3">{t('common:fillDescriptionDetails')}*</Card.Title>
          <TrixEditor
            // key={key}
            className="trix-content"
            onChange={(html) => setFieldValue('descriptionHtml', html)}
            onEditorReady={noob}
            mergeTags={[]}
            value={values.descriptionHtml}
            placeholder={t('common:description')}
          />
        </Card.Body>

        <Card.Footer className="bg-white border-0">
          <div className="text-center my-4">
            <Button type="submit" variant="primary" size="lg" disabled={isSubmitting}>
              <span className="me-2">
                {t('common:summary')} & {t('common:review')}
              </span>
              <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </div>
        </Card.Footer>
      </Card>
    </Form>
  );
};

export { InsurancePolicyBuyForm };
